// src/screens/ConfigScreen.jsx
import { useWalletId } from "../hooks/useWalletId";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  FiUser,
  FiLogOut,
  FiSave,
  FiTrash2,
  FiPlus,
  FiX,
  FiSearch,
  FiShield,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../api";
import { useAuth } from "../auth/AuthProvider";
import { useSettings } from "../settings";

import PullToRefreshHeader from "../components/PullToRefreshHeader";
import { usePullToRefresh } from "../hooks/usePullToRefresh";

/* ============================================================
   Vocabulário oficial (UI)
============================================================ */
const DISCLAIMER_EXECUTION =
  "O FinBot não executa operações financeiras nem envia ordens ao mercado.";

const SIDE_LABEL = { BUY: "Compra", SELL: "Venda" };
const SIDE_OPTIONS = ["BUY", "SELL"];

/* ============================================================
   Helpers
============================================================ */
function isoToLocalInputValue(isoOrDate) {
  if (!isoOrDate) return "";
  const d = typeof isoOrDate === "string" ? new Date(isoOrDate) : isoOrDate;
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

function localInputValueToIso(v) {
  // v: "YYYY-MM-DDTHH:mm"
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function fmtBRL(x) {
  const n = Number(x);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function fmtNum(x, digits = 8) {
  const n = Number(x);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });
}

function parseDecimalBR(v) {
  if (v == null) return NaN;
  const s = String(v).trim();
  if (!s) return NaN;

  // remove espaços e separador de milhar comum, troca vírgula por ponto
  const norm = s
    .replace(/\s+/g, "")
    .replace(/\.(?=\d{3}(\D|$))/g, "") // remove pontos de milhar tipo 1.234,56
    .replace(",", ".");

  const n = Number(norm);
  return Number.isFinite(n) ? n : NaN;
}

function fmtDateTimeLocal(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("pt-BR", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/* ============================================================
   ConfigScreen (Conta & Carteira)
============================================================ */
export default function ConfigScreen() {
  const nav = useNavigate();
  const { logout, auth } = useAuth();

  // ✅ settings locais: TopK + selectedWalletId
  const { settings, update, clearLocal } = useSettings();

  // 🔑 base do auth (para fallback inteligente)
const authWalletId = useMemo(() => {
  return auth?.defaultWalletId ?? null;
}, [auth?.defaultWalletId]);

  // ✅ walletId efetivo: prioriza seleção local
  const walletId = useWalletId();

  const profile = useMemo(() => {
    return {
      email: auth?.email || null,
      customerId: auth?.customerId ?? null,
      walletId,
    };
  }, [auth, walletId]);

  function onLogout() {
    logout();
    nav("/login", { replace: true });
  }

  async function openPrivacy() {
    const url = "https://fin.montebrasil.com.br/privacy";

    // Detecta runtime do Capacitor sem precisar importar @capacitor/core
    const isNative =
      typeof window !== "undefined" &&
      !!window.Capacitor &&
      typeof window.Capacitor.isNativePlatform === "function" &&
      window.Capacitor.isNativePlatform();

    if (isNative) {
      try {
        // Importa só no runtime nativo (não quebra build web)
        const mod = await import("@capacitor/browser");
        await mod.Browser.open({ url });
        return;
      } catch (e) {
        console.warn("Capacitor Browser.open failed, fallback to location", e);
        window.location.href = url;
        return;
      }
    }

    // Web: abre rota interna
    nav("/privacy");
  }

  // ---------------------------
  // Carteiras (lista + seleção local)
  // ---------------------------
  const [wallets, setWallets] = useState([]);
  const [walletsBusy, setWalletsBusy] = useState(false);

  async function loadWallets() {
    setWalletsBusy(true);
    try {
      const r = await apiFetch("/api/wallets");
      const items = Array.isArray(r?.items) ? r.items : [];
      setWallets(items);
      return items;
    } catch (e) {
      console.error("loadWallets failed", e);
      setWallets([]);
      return [];
    } finally {
      setWalletsBusy(false);
    }
  }

  // carrega carteiras ao entrar (ou ao trocar usuário)
  useEffect(() => {
    if (!auth?.customerId) return;
    loadWallets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.customerId]);

  // fallback inteligente: se selectedWalletId não existe/é inválido, escolhe uma válida
  useEffect(() => {
    const items = Array.isArray(wallets) ? wallets : [];
    if (!items.length) return;

    const ids = new Set(items.map((w) => Number(w?.id)).filter((x) => Number.isFinite(x) && x > 0));

    const selected = Number(settings?.selectedWalletId);
    const hasSelected = Number.isFinite(selected) && selected > 0 && ids.has(selected);

    if (hasSelected) return;

    // tenta usar o authWalletId se estiver na lista, senão pega a primeira
    const preferred = ids.has(Number(authWalletId)) ? Number(authWalletId) : Number(items[0]?.id);
    if (Number.isFinite(preferred) && preferred > 0) {
      update({ selectedWalletId: preferred });
    }
  }, [wallets, settings?.selectedWalletId, authWalletId, update]);

  // ---------------------------
  // Excluir conta (hard delete)
  // ---------------------------
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteText, setDeleteText] = useState("");

  async function confirmDeleteAccount() {
    if (deleteBusy) return;

    const t = String(deleteText || "").trim().toUpperCase();
    if (t !== "EXCLUIR") {
      alert('Digite "EXCLUIR" para confirmar.');
      return;
    }

    setDeleteBusy(true);
    try {
      const r = await apiFetch("/api/account", { method: "DELETE" });
      if (!r?.ok) throw new Error(r?.error || "failed_to_delete_account");

      setDeleteOpen(false);
      setDeleteText("");

      logout();
      nav("/login", { replace: true });
    } catch (e) {
      console.error("confirmDeleteAccount failed", e);
      alert("Não foi possível excluir sua conta. Tente novamente.");
    } finally {
      setDeleteBusy(false);
    }
  }

  // ---------------------------
  // ✅ Preferências: TopK (front-end)
  // ---------------------------
  const topK = Number(settings?.planTopK ?? 5);

  // ---------------------------
  // Wallet summary + Cash adjust
  // ---------------------------
  const [summary, setSummary] = useState(null);
  const [cashDelta, setCashDelta] = useState(""); // BRL
  const [cashBusy, setCashBusy] = useState(false);

  async function loadSummary() {
    try {
      const r = await apiFetch(`/api/wallet/summary?walletId=${walletId}`);
      if (!r?.ok) throw new Error(r?.error || "failed_wallet_summary");
      setSummary(r);
    } catch (e) {
      console.error("loadSummary failed", e);
      setSummary(null);
    }
  }

  useEffect(() => {
    loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletId]);

  async function applyCashDelta() {
    if (cashBusy) return;

    const delta = parseDecimalBR(cashDelta);

    if (!Number.isFinite(delta) || delta === 0) {
      alert("Informe um valor válido (ex.: 1000 ou -1000).");
      return;
    }

    setCashBusy(true);
    try {
      const r = await apiFetch("/api/wallet/cash/adjust", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ walletId, delta }),
      });

      if (!r?.ok) throw new Error(r?.error || "failed_to_adjust_cash");

      setCashDelta("");
      await loadSummary();
    } catch (e) {
      console.error("applyCashDelta failed", e);
      const msg = String(e?.message || "").includes("cash_would_be_negative")
        ? "Este ajuste deixaria o caixa negativo."
        : "Não foi possível ajustar o caixa.";
      alert(msg);
    } finally {
      setCashBusy(false);
    }
  }

  // ---------------------------
  // Trades: lista compacta + editor sob demanda
  // ---------------------------
  const [assets, setAssets] = useState([]);
  const [trades, setTrades] = useState([]);
  const [tradesBusy, setTradesBusy] = useState(false);

  const [filterAssetId, setFilterAssetId] = useState("ALL");
  const [searchText, setSearchText] = useState("");

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState("edit"); // "new" | "edit"
  const [editorTrade, setEditorTrade] = useState(null);
  const [editorBusy, setEditorBusy] = useState(false);

  async function loadAssets() {
    try {
      const r = await apiFetch("/api/assets");
      if (Array.isArray(r)) setAssets(r);
      else setAssets([]);
    } catch (e) {
      console.error("loadAssets failed", e);
      setAssets([]);
    }
  }

  async function loadTrades() {
    setTradesBusy(true);
    try {
      const r = await apiFetch(`/api/wallet/trades?walletId=${walletId}`);
      if (Array.isArray(r)) setTrades(r);
      else setTrades([]);
    } catch (e) {
      console.error("loadTrades failed", e);
      setTrades([]);
    } finally {
      setTradesBusy(false);
    }
  }

  useEffect(() => {
    loadAssets();
    loadTrades();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletId]);

  const assetsById = useMemo(() => {
    const m = new Map();
    for (const a of assets || []) m.set(Number(a.id), a);
    return m;
  }, [assets]);

  const filteredTrades = useMemo(() => {
    let rows = Array.isArray(trades) ? trades.slice() : [];

    // filtro por ativo
    if (filterAssetId !== "ALL") {
      const aid = Number(filterAssetId);
      rows = rows.filter((t) => Number(t.asset_id) === aid);
    }

    // busca simples (símbolo/nome)
    const q = String(searchText || "").trim().toLowerCase();
    if (q) {
      rows = rows.filter((t) => {
        const sym = String(t.asset_symbol || "").toLowerCase();
        const nm = String(t.asset_name || "").toLowerCase();
        return sym.includes(q) || nm.includes(q);
      });
    }

    // ordena por data desc
    rows.sort((a, b) => {
      const da = new Date(a.created_at).getTime();
      const db = new Date(b.created_at).getTime();
      if (db !== da) return db - da;
      return Number(b.id) - Number(a.id);
    });

    return rows;
  }, [trades, filterAssetId, searchText]);

  function openNewTrade() {
    const defaultAsset = assets?.[0] || null;
    setEditorMode("new");
    setEditorTrade({
      id: null,
      wallet_id: walletId,
      asset_id: defaultAsset ? defaultAsset.id : null,
      side: "BUY",
      qty: "",
      price: "",
      created_at: new Date().toISOString(),
    });
    setEditorOpen(true);
  }

  function openEditTrade(t) {
    setEditorMode("edit");
    setEditorTrade({
      id: t.id,
      wallet_id: t.wallet_id,
      asset_id: t.asset_id,
      side: t.side,
      qty: t.qty,
      price: t.price,
      created_at: t.created_at,
    });
    setEditorOpen(true);
  }

  async function saveTrade() {
    if (!editorTrade) return;

    const assetId = Number(editorTrade.asset_id);
    const side = String(editorTrade.side || "").toUpperCase();
    const qty = parseDecimalBR(editorTrade.qty);
    const price = parseDecimalBR(editorTrade.price);

    const created_at = editorTrade.created_at ? new Date(editorTrade.created_at) : null;

    if (!assetId) return alert("Selecione um ativo.");
    if (!SIDE_OPTIONS.includes(side)) return alert("Selecione Compra/Venda.");
    if (!Number.isFinite(qty) || qty <= 0) return alert("Qtd inválida.");
    if (!Number.isFinite(price) || price <= 0) return alert("Preço inválido.");
    if (!created_at || Number.isNaN(created_at.getTime())) return alert("Data/Hora inválida.");

    setEditorBusy(true);
    try {
      if (editorMode === "new") {
        const r = await apiFetch("/api/wallet/trades", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            walletId,
            assetId,
            side,
            qty,
            price,
            created_at: created_at.toISOString(),
          }),
        });
        if (!r?.ok) throw new Error(r?.error || "failed_to_create_trade");
      } else {
        const r = await apiFetch(`/api/wallet/trades/${editorTrade.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            assetId,
            side,
            qty,
            price,
            created_at: created_at.toISOString(),
          }),
        });
        if (!r?.ok) throw new Error(r?.error || "failed_to_update_trade");
      }

      const rr = await apiFetch("/api/wallet/recompute", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ walletId }),
      });
      if (!rr?.ok) throw new Error(rr?.error || "failed_to_recompute");

      setEditorOpen(false);
      await loadTrades();
      await loadSummary();
    } catch (e) {
      console.error("saveTrade failed", e);
      alert("Não foi possível salvar a operação.");
    } finally {
      setEditorBusy(false);
    }
  }

  async function deleteTrade() {
    if (!editorTrade?.id) return;
    if (!window.confirm("Remover esta operação?")) return;

    setEditorBusy(true);
    try {
      const r = await apiFetch(`/api/wallet/trades/${editorTrade.id}`, {
        method: "DELETE",
      });
      if (!r?.ok) throw new Error(r?.error || "failed_to_delete_trade");

      const rr = await apiFetch("/api/wallet/recompute", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ walletId }),
      });
      if (!rr?.ok) throw new Error(rr?.error || "failed_to_recompute");

      setEditorOpen(false);
      await loadTrades();
      await loadSummary();
    } catch (e) {
      console.error("deleteTrade failed", e);
      alert("Não foi possível remover a operação.");
    } finally {
      setEditorBusy(false);
    }
  }

  // =========================
  // ✅ Pull-to-refresh (perene)
  // =========================
  const refreshAll = useCallback(async () => {
    await Promise.allSettled([loadWallets(), loadSummary(), loadAssets(), loadTrades()]);
  }, [walletId]); // walletId influencia as rotas internas (ok)

  const ptr = usePullToRefresh({
    enabled: true,
    isBlocked: false,
    startZoneY: 260,
    maxPull: 110,
    readyPull: 70,
    refreshHeight: 56,
    onRefresh: refreshAll,
  });

  // ---------------------------
  // Render
  // ---------------------------
  return (
    <div
      style={{ padding: 16, maxWidth: 980, margin: "0 auto" }}
      onTouchStart={ptr.handlers.onTouchStart}
      onTouchMove={ptr.handlers.onTouchMove}
      onTouchEnd={ptr.handlers.onTouchEnd}
      onTouchCancel={ptr.handlers.onTouchEnd}
    >
      <PullToRefreshHeader height={ptr.height} refreshing={ptr.refreshing} ready={ptr.ready} />

      {/* =========================
          PERFIL
      ========================= */}
      <div style={styles.profileCard}>
        <div style={styles.profileTopRow}>
          <div style={{ display: "flex", gap: 12, alignItems: "center", minWidth: 0 }}>
            <div style={styles.avatar}>
              <FiUser size={18} />
            </div>

            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 14, opacity: 0.75, marginBottom: 2 }}>Perfil</div>

              <div style={styles.profileEmail} title={profile.email || "Conta FinBot"}>
                {profile.email || "Conta FinBot"}
              </div>

              <div style={{ fontSize: 12, opacity: 0.65, marginTop: 4 }}>
                {profile.customerId != null ? `Usuário #${profile.customerId}` : "—"}
                {profile.walletId != null ? ` • Carteira #${profile.walletId}` : ""}
              </div>
            </div>
          </div>

          <div style={styles.profileActions}>
            <button onClick={openPrivacy} style={styles.privacyBtn} title="Política de Privacidade" type="button">
              <FiShield />
              Privacidade
            </button>

            <button onClick={onLogout} style={styles.logoutBtn} title="Sair" type="button">
              <FiLogOut />
              Sair
            </button>

            <button
              onClick={() => {
                setDeleteText("");
                setDeleteOpen(true);
              }}
              style={{ ...styles.dangerBtn, height: 40 }}
              title="Excluir conta"
              type="button"
            >
              <FiTrash2 />
              Excluir conta
            </button>
          </div>
        </div>

        <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
          <div style={{ fontSize: 12, opacity: 0.7, lineHeight: 1.35 }}>
            Conta & Carteira — ajuste de preferências e estado inicial (histórico de operações).
          </div>

          <div style={styles.disclaimerBox}>
            <b style={{ opacity: 0.92 }}>Aviso:</b> {DISCLAIMER_EXECUTION}
          </div>
        </div>
      </div>

      {/* =========================
          Carteira ativa
      ========================= */}
      <section style={styles.section}>
        <div style={styles.sectionHeader}>
          <div style={styles.sectionTitle}>Carteira ativa</div>

          <button onClick={loadWallets} style={styles.ghostBtn} title="Atualizar carteiras" type="button">
            {walletsBusy ? "Atualizando..." : "Atualizar"}
          </button>
        </div>

        <div style={{ display: "grid", gap: 10 }}>
          <div style={{ fontSize: 12, opacity: 0.72, lineHeight: 1.35 }}>
            Selecione a carteira que o FinBot vai usar nesta sessão (saldo, trades e relatórios).
          </div>

          <label style={styles.field}>
            <span style={styles.fieldLabel}>Carteira</span>
            <select
              value={String(settings?.selectedWalletId ?? "")}
              onChange={(e) => update({ selectedWalletId: e.target.value })}

              style={styles.select}
              disabled={!wallets?.length}
            >
              {(wallets || []).length === 0 ? (
                <option value="">
                  {walletsBusy ? "Carregando..." : "Nenhuma carteira encontrada"}
                </option>
              ) : (
                (wallets || []).map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name ? `${w.name} — #${w.id}` : `Carteira #${w.id}`}
                  </option>
                ))
              )}
            </select>
          </label>
        </div>
      </section>

      {/* =========================
          Preferências (TopK apenas)
      ========================= */}
      <section style={{ ...styles.section, marginTop: 12 }}>
        <div style={styles.sectionHeader}>
          <div style={styles.sectionTitle}>Preferências</div>

          <button
            onClick={() => {
              if (window.confirm("Resetar preferências locais (TopK e carteira ativa) para o padrão?")) {
                clearLocal?.();
              }
            }}
            style={styles.ghostBtn}
            title="Resetar preferências"
            type="button"
          >
            Reset
          </button>
        </div>

        <div style={{ display: "grid", gap: 12 }}>
          <label style={styles.field}>
            <span style={styles.fieldLabel}>TopK (quantos ativos no plano do dia)</span>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 90px", gap: 12, alignItems: "center" }}>
              <input
                type="range"
                min="1"
                max="20"
                step="1"
                value={topK}
                onChange={(e) => update({ planTopK: Number(e.target.value) })}
                style={{ width: "100%" }}
              />

              <input
                type="number"
                min="1"
                max="50"
                step="1"
                value={topK}
                onChange={(e) => update({ planTopK: Number(e.target.value) })}
                style={{
                  ...styles.input,
                  textAlign: "right",
                  border: "1px solid rgba(255,255,255,0.10)",
                }}
              />
            </div>

            <div style={{ marginTop: 6, fontSize: 12, opacity: 0.72, lineHeight: 1.35 }}>
              Este parâmetro afeta apenas a quantidade de ativos sugeridos no plano/ações. A estratégia (parâmetros do motor)
              está no backend.
            </div>
          </label>
        </div>
      </section>

      {/* =========================
          Caixa disponível (delta)
      ========================= */}
      <section style={{ ...styles.section, marginTop: 12 }}>
        <div style={styles.sectionHeader}>
          <div style={styles.sectionTitle}>Caixa disponível</div>
          <button onClick={loadSummary} style={styles.ghostBtn} title="Atualizar" type="button">
            Atualizar
          </button>
        </div>

        <div style={{ display: "grid", gap: 12 }}>
          <div style={styles.cashRow}>
            <div style={{ fontSize: 12, opacity: 0.7 }}>Caixa atual</div>
            <div style={{ fontSize: 18, fontWeight: 900 }}>{summary ? fmtBRL(summary.cash_available) : "—"}</div>
          </div>

          <div style={{ fontSize: 12, opacity: 0.72, lineHeight: 1.35 }}>
            Informe um ajuste no caixa disponível. Use <b>valor positivo</b> para entrada de dinheiro e <b>valor negativo</b>{" "}
            para retirada. (Este ajuste não cria trade.)
          </div>

          <div style={styles.cashAdjustGrid}>
            <label style={styles.field}>
              <span style={styles.fieldLabel}>Ajuste (R$)</span>
              <input
                type="text"
                inputMode="decimal"
                pattern="[0-9.,-]*"
                value={cashDelta}
                onChange={(e) => setCashDelta(e.target.value)}
                style={{ ...styles.input, textAlign: "right" }}
              />
            </label>

            <button
              onClick={applyCashDelta}
              style={{
                ...styles.primaryBtn,
                height: 52,
                opacity: cashBusy ? 0.7 : 1,
                cursor: cashBusy ? "not-allowed" : "pointer",
              }}
              disabled={cashBusy}
              title="Aplicar ajuste no caixa"
              type="button"
            >
              {cashBusy ? "Aplicando..." : "Aplicar"}
            </button>
          </div>
        </div>
      </section>

      {/* =========================
          Trades (Tabela compacta)
      ========================= */}
      <section style={{ ...styles.section, marginTop: 12 }}>
        <div style={styles.sectionHeader}>
          <div>
            <div style={styles.sectionTitle}>Histórico de operações (Trades)</div>
            <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>
              Use a tabela para localizar e editar rapidamente. Ao salvar/remover, o FinBot recalcula as posições.
            </div>
          </div>

          <button onClick={openNewTrade} style={styles.primaryBtn} title="Nova operação" type="button">
            <FiPlus />
            Novo
          </button>
        </div>

        <div style={styles.tableTools}>
          <label style={styles.smallField}>
            <span style={styles.smallLabel}>Ativo</span>
            <select value={filterAssetId} onChange={(e) => setFilterAssetId(e.target.value)} style={styles.select}>
              <option value="ALL">Todos</option>
              {(assets || []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.symbol} — {a.name}
                </option>
              ))}
            </select>
          </label>

          <label style={styles.searchField}>
            <FiSearch style={{ opacity: 0.7 }} />
            <input
              type="text"
              placeholder="Filtrar por símbolo/nome…"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={styles.searchInput}
            />
          </label>

          <div style={{ flex: 1 }} />

          <button onClick={loadTrades} style={styles.ghostBtn} title="Atualizar lista" type="button">
            {tradesBusy ? "Atualizando..." : "Atualizar"}
          </button>
        </div>

        <div style={{ marginTop: 10, overflow: "auto" }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Data/Hora</th>
                <th style={styles.th}>Ativo</th>
                <th style={styles.th}>Lado</th>
                <th style={{ ...styles.th, textAlign: "right" }}>Qtd</th>
                <th style={{ ...styles.th, textAlign: "right" }}>Preço</th>
                <th style={{ ...styles.th, textAlign: "right" }}>Valor</th>
              </tr>
            </thead>
            <tbody>
              {filteredTrades.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ padding: 14, opacity: 0.7 }}>
                    {tradesBusy ? "Carregando…" : "Nenhuma operação encontrada."}
                  </td>
                </tr>
              ) : (
                filteredTrades.map((t) => {
                  const a = assetsById.get(Number(t.asset_id));
                  const sym = t.asset_symbol || a?.symbol || `#${t.asset_id}`;
                  const nm = t.asset_name || a?.name || "";
                  const sideLabel = SIDE_LABEL[String(t.side || "").toUpperCase()] || t.side;

                  return (
                    <tr key={t.id} style={styles.tr} onClick={() => openEditTrade(t)} title="Clique para editar">
                      <td style={styles.td}>{fmtDateTimeLocal(t.created_at)}</td>
                      <td style={styles.td}>
                        <div style={{ fontWeight: 900 }}>{sym}</div>
                        <div style={{ fontSize: 12, opacity: 0.65 }}>{nm}</div>
                      </td>
                      <td style={styles.td}>{sideLabel}</td>
                      <td style={{ ...styles.td, textAlign: "right" }}>{fmtNum(t.qty, 1)}</td>
                      <td style={{ ...styles.td, textAlign: "right" }}>{fmtNum(t.price, 2)}</td>
                      <td style={{ ...styles.td, textAlign: "right" }}>{fmtNum(t.trade_value, 2)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* =========================
          Editor (modal simples)
      ========================= */}
      {editorOpen ? (
        <Modal onClose={() => !editorBusy && setEditorOpen(false)}>
          <div style={styles.modalHeader}>
            <div style={{ fontWeight: 900, fontSize: 16 }}>
              {editorMode === "new" ? "Nova operação" : "Editar operação"}
            </div>
            <button onClick={() => !editorBusy && setEditorOpen(false)} style={styles.iconBtn} title="Fechar" type="button">
              <FiX />
            </button>
          </div>

          <div style={{ display: "grid", gap: 12 }}>
            <label style={styles.field}>
              <span style={styles.fieldLabel}>Ativo</span>
              <select
                value={editorTrade?.asset_id ?? ""}
                onChange={(e) =>
                  setEditorTrade((p) => ({
                    ...(p || {}),
                    asset_id: Number(e.target.value),
                  }))
                }
                style={styles.select}
              >
                <option value="" disabled>
                  Selecione…
                </option>
                {(assets || []).map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.symbol} — {a.name}
                  </option>
                ))}
              </select>
            </label>

            <label style={styles.field}>
              <span style={styles.fieldLabel}>Lado</span>
              <select
                value={editorTrade?.side ?? "BUY"}
                onChange={(e) => setEditorTrade((p) => ({ ...(p || {}), side: e.target.value }))}
                style={styles.select}
              >
                <option value="BUY">Compra</option>
                <option value="SELL">Venda</option>
              </select>
            </label>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field
                label="Qtd"
                value={editorTrade?.qty ?? ""}
                step="0.00000001"
                min="0"
                onChange={(v) => setEditorTrade((p) => ({ ...(p || {}), qty: v }))}
              />
              <Field
                label="Preço"
                value={editorTrade?.price ?? ""}
                step="0.00000001"
                min="0"
                onChange={(v) => setEditorTrade((p) => ({ ...(p || {}), price: v }))}
              />
            </div>

            <label style={styles.field}>
              <span style={styles.fieldLabel}>Data/Hora</span>
              <input
                type="datetime-local"
                value={isoToLocalInputValue(editorTrade?.created_at)}
                onChange={(e) =>
                  setEditorTrade((p) => ({
                    ...(p || {}),
                    created_at: localInputValueToIso(e.target.value),
                  }))
                }
                style={{ ...styles.input, textAlign: "left" }}
              />
            </label>

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 4 }}>
              {editorMode === "edit" ? (
                <button
                  onClick={deleteTrade}
                  style={{ ...styles.dangerBtn, opacity: editorBusy ? 0.7 : 1 }}
                  disabled={editorBusy}
                  title="Remover operação"
                  type="button"
                >
                  <FiTrash2 />
                  Remover
                </button>
              ) : null}

              <button
                onClick={saveTrade}
                style={{ ...styles.primaryBtn, opacity: editorBusy ? 0.7 : 1 }}
                disabled={editorBusy}
                title="Salvar operação"
                type="button"
              >
                <FiSave />
                {editorBusy ? "Salvando..." : "Salvar"}
              </button>
            </div>
          </div>
        </Modal>
      ) : null}

      {/* =========================
          Excluir conta (modal)
      ========================= */}
      {deleteOpen ? (
        <Modal onClose={() => !deleteBusy && setDeleteOpen(false)}>
          <div style={styles.modalHeader}>
            <div style={{ fontWeight: 900, fontSize: 16 }}>Excluir conta</div>
            <button onClick={() => !deleteBusy && setDeleteOpen(false)} style={styles.iconBtn} title="Fechar" type="button">
              <FiX />
            </button>
          </div>

          <div style={{ display: "grid", gap: 12 }}>
            <div
              style={{
                padding: 12,
                borderRadius: 14,
                border: "1px solid rgba(255,70,70,0.35)",
                background: "rgba(255,70,70,0.10)",
                color: "rgba(255,255,255,0.92)",
                lineHeight: 1.35,
                fontSize: 13,
              }}
            >
              Esta ação é <b>irreversível</b>. Seus dados serão removidos do FinBot (conta, preferências e histórico da
              carteira).
            </div>

            <div style={styles.disclaimerBox}>
              <b style={{ opacity: 0.92 }}>Aviso:</b> {DISCLAIMER_EXECUTION}
            </div>

            <label style={styles.field}>
              <span style={styles.fieldLabel}>Para confirmar, digite EXCLUIR</span>
              <input
                type="text"
                value={deleteText}
                onChange={(e) => setDeleteText(e.target.value)}
                style={{ ...styles.input, textAlign: "left" }}
                placeholder="EXCLUIR"
                autoFocus
                disabled={deleteBusy}
              />
            </label>

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                onClick={() => !deleteBusy && setDeleteOpen(false)}
                style={styles.ghostBtn}
                disabled={deleteBusy}
                title="Cancelar"
                type="button"
              >
                Cancelar
              </button>

              <button
                onClick={confirmDeleteAccount}
                style={{ ...styles.dangerBtn, opacity: deleteBusy ? 0.7 : 1 }}
                disabled={deleteBusy}
                title="Excluir conta definitivamente"
                type="button"
              >
                <FiTrash2 />
                {deleteBusy ? "Excluindo..." : "Excluir definitivamente"}
              </button>
            </div>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}

/* ============================================================
   Componentes locais
============================================================ */

function Field({ label, value, step, min, max, placeholder, onChange }) {
  return (
    <label style={styles.field}>
      <span style={styles.fieldLabel}>{label}</span>
      <input
        type="text"
        inputMode="decimal"
        pattern="[0-9.,-]*"
        placeholder={placeholder}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...styles.input, textAlign: "right" }}
      />
    </label>
  );
}

// Mantido (mesmo não usado) para preservar o arquivo “intacto”
function PercentField({ label, valueFrac, stepPct = "0.5", minPct = "0", maxPct = "100", onChangeFrac }) {
  const valuePct = valueFrac == null ? "" : Number(valueFrac) * 100;

  return (
    <label style={styles.field}>
      <span style={styles.fieldLabel}>{label}</span>
      <div style={styles.inputWrap}>
        <input
          type="number"
          inputMode="decimal"
          step={stepPct}
          min={minPct}
          max={maxPct}
          value={Number.isFinite(valuePct) ? valuePct : ""}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "") return onChangeFrac(0);
            const pct = Number(raw);
            if (!Number.isFinite(pct)) return;
            onChangeFrac(pct / 100);
          }}
          style={styles.inputPercent}
        />
        <span style={styles.suffixPct}>%</span>
      </div>
    </label>
  );
}

function Modal({ children, onClose }) {
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose?.();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div style={styles.modalOverlay} onMouseDown={onClose}>
      <div style={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

/* ============================================================
   Estilos
============================================================ */
const styles = {
  profileCard: {
    borderRadius: 16,
    padding: 14,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(255,255,255,0.04)",
    boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
    marginBottom: 14,
  },
  profileTopRow: {
    display: "flex",
    gap: 12,
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
  },
  profileActions: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flex: "0 0 auto",
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },
  privacyBtn: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.14)",
    background: "rgba(0,0,0,0.12)",
    color: "rgba(255,255,255,0.92)",
    fontWeight: 900,
    cursor: "pointer",
    whiteSpace: "nowrap",
    height: 40,
  },
  avatar: {
    width: 44,
    height: 44,
    borderRadius: 14,
    display: "grid",
    placeItems: "center",
    border: "1px solid rgba(255,255,255,0.14)",
    background: "rgba(0,0,0,0.25)",
    flex: "0 0 auto",
  },
  profileEmail: {
    fontSize: 16,
    fontWeight: 800,
    lineHeight: 1.2,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: 520,
  },
  logoutBtn: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.2)",
    background: "rgba(0,0,0,0.20)",
    color: "white",
    fontWeight: 800,
    cursor: "pointer",
    whiteSpace: "nowrap",
    flex: "0 0 auto",
    height: 40,
  },

  disclaimerBox: {
    padding: "10px 12px",
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,0.10)",
    background: "rgba(0,0,0,0.16)",
    color: "rgba(255,255,255,0.86)",
    fontSize: 12,
    lineHeight: 1.35,
  },

  section: {
    padding: 12,
    borderRadius: 16,
    border: "1px solid rgba(255,255,255,0.10)",
    background: "rgba(255,255,255,0.035)",
    boxShadow: "0 14px 40px rgba(0,0,0,0.35)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
  },
  sectionHeader: {
    display: "flex",
    gap: 12,
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 10,
  },
  sectionTitle: {
    fontWeight: 900,
    color: "rgba(255,255,255,0.90)",
    letterSpacing: 0.2,
  },

  primaryBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: "10px 14px",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.16)",
    background: "rgba(255,255,255,0.92)",
    color: "rgba(0,0,0,0.90)",
    fontWeight: 900,
    height: 40,
    whiteSpace: "nowrap",
    cursor: "pointer",
  },
  dangerBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: "10px 14px",
    borderRadius: 12,
    border: "1px solid rgba(255,70,70,0.35)",
    background: "rgba(255,70,70,0.12)",
    color: "rgba(255,255,255,0.90)",
    fontWeight: 900,
    height: 40,
    whiteSpace: "nowrap",
    cursor: "pointer",
  },
  ghostBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.14)",
    background: "rgba(0,0,0,0.20)",
    color: "rgba(255,255,255,0.90)",
    fontWeight: 800,
    height: 40,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },

  field: {
    display: "grid",
    gap: 6,
    padding: "10px 12px",
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,0.10)",
    background: "rgba(0,0,0,0.18)",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06)",
  },
  fieldLabel: {
    fontSize: 12,
    color: "rgba(255,255,255,0.72)",
  },
  input: {
    width: "100%",
    padding: "10px 10px",
    borderRadius: 12,
    border: "1px solid transparent",
    background: "transparent",
    color: "rgba(255,255,255,0.92)",
    outline: "none",
    fontSize: 16,
    lineHeight: "20px",
    boxSizing: "border-box",
  },
  select: {
    width: "100%",
    padding: "10px 10px",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.10)",
    background: "rgba(0,0,0,0.10)",
    color: "rgba(255,255,255,0.92)",
    outline: "none",
    fontSize: 16,
    lineHeight: "20px",
    boxSizing: "border-box",
  },

  inputWrap: {
    position: "relative",
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  inputPercent: {
    flex: 1,
    minWidth: 0,
    padding: "10px 10px",
    borderRadius: 12,
    border: "1px solid transparent",
    background: "transparent",
    color: "rgba(255,255,255,0.92)",
    outline: "none",
    fontSize: 16,
    lineHeight: "20px",
    boxSizing: "border-box",
    textAlign: "right",
  },
  suffixPct: {
    flex: "0 0 auto",
    padding: "6px 10px",
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,0.10)",
    background: "rgba(255,255,255,0.06)",
    color: "rgba(255,255,255,0.80)",
    fontSize: 12,
    letterSpacing: 0.3,
    userSelect: "none",
  },

  cashRow: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    padding: "10px 12px",
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,0.10)",
    background: "rgba(0,0,0,0.18)",
  },
  cashAdjustGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 220px",
    gap: 12,
  },

  tableTools: {
    display: "flex",
    gap: 10,
    alignItems: "end",
    flexWrap: "wrap",
  },
  smallField: {
    display: "grid",
    gap: 6,
    minWidth: 260,
  },
  smallLabel: {
    fontSize: 12,
    opacity: 0.72,
  },
  searchField: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 12px",
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,0.10)",
    background: "rgba(0,0,0,0.18)",
    minWidth: 280,
  },
  searchInput: {
    flex: 1,
    minWidth: 0,
    border: "none",
    outline: "none",
    background: "transparent",
    color: "rgba(255,255,255,0.92)",
    fontSize: 14,
  },

  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    minWidth: 760,
    overflow: "hidden",
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,0.10)",
    background: "rgba(0,0,0,0.14)",
  },
  th: {
    textAlign: "left",
    fontSize: 12,
    letterSpacing: 0.2,
    opacity: 0.75,
    padding: "10px 12px",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
    position: "sticky",
    top: 0,
    background: "rgba(10,10,12,0.55)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    zIndex: 1,
  },
  tr: { cursor: "pointer" },
  td: {
    padding: "10px 12px",
    borderBottom: "1px solid rgba(255,255,255,0.06)",
    verticalAlign: "top",
    color: "rgba(255,255,255,0.92)",
  },

  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.55)",
    display: "grid",
    placeItems: "center",
    padding: 16,
    zIndex: 50,
  },
  modal: {
    width: "min(720px, 100%)",
    borderRadius: 16,
    border: "1px solid rgba(255,255,255,0.14)",
    background: "rgba(15,15,18,0.92)",
    boxShadow: "0 22px 70px rgba(0,0,0,0.55)",
    padding: 14,
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
  },
  modalHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 10,
  },
  iconBtn: {
    width: 40,
    height: 40,
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.14)",
    background: "rgba(0,0,0,0.20)",
    color: "rgba(255,255,255,0.92)",
    cursor: "pointer",
    display: "grid",
    placeItems: "center",
  },
};
